import type { VacationOverviewDocument } from '../../models/vacationOverview.types';
import { toIsoDay } from '../overviewDates';

const MONTH_LABELS = [
	'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
	'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez',
];

export interface DeadlineMonth {
	month: string;
	shortLabel: string;
	monthNum: number;
	year: number;
	key: string;
	isPast: boolean;
	expired: number;
	upcoming: number;
}

export function buildDeadlinesByMonth(
	documents: VacationOverviewDocument[],
	referenceDate: Date,
	monthsAhead: number = 12
): DeadlineMonth[] {
	const todayIso = toIsoDay(referenceDate);
	const activeBase = documents.filter(d => d.active && d.inBase);
	const monthsMap = new Map<string, { year: number; month: number; expired: number; upcoming: number }>();

	activeBase.forEach(doc => {
		if (!doc.balance?.nextDeadline) return;
		const deadline = doc.balance.nextDeadline;
		const year = Number(deadline.slice(0, 4));
		const month = Number(deadline.slice(5, 7));
		const key = `${year}-${String(month).padStart(2, '0')}`;

		if (!monthsMap.has(key)) {
			monthsMap.set(key, { year, month, expired: 0, upcoming: 0 });
		}

		const entry = monthsMap.get(key)!;
		if (deadline < todayIso) {
			entry.expired++;
		} else {
			entry.upcoming++;
		}
	});

	const futureKeys = new Set<string>();
	const futureMonths: DeadlineMonth[] = [];
	const currentYearMonth = `${referenceDate.getFullYear()}-${String(referenceDate.getMonth() + 1).padStart(2, '0')}`;

	for (let i = 0; i < monthsAhead; i++) {
		const date = new Date(referenceDate.getFullYear(), referenceDate.getMonth() + i, 1);
		const year = date.getFullYear();
		const month = date.getMonth() + 1;
		const key = `${year}-${String(month).padStart(2, '0')}`;
		futureKeys.add(key);

		const monthLabel = new Date(year, month - 1).toLocaleString('pt-BR', {
			month: 'short',
			year: 'numeric',
		});
		const entry = monthsMap.get(key);

		futureMonths.push({
			month: monthLabel,
			shortLabel: `${MONTH_LABELS[month - 1]}/${String(year).slice(2)}`,
			monthNum: month,
			year,
			key,
			isPast: false,
			expired: entry?.expired ?? 0,
			upcoming: entry?.upcoming ?? 0,
		});
	}

	const pastMonths: DeadlineMonth[] = Array.from(monthsMap.entries())
		.filter(([key]) => key < currentYearMonth)
		.map(([key, entry]) => ({
			month: new Date(entry.year, entry.month - 1).toLocaleString('pt-BR', {
				month: 'short',
				year: 'numeric',
			}),
			shortLabel: `${MONTH_LABELS[entry.month - 1]}/${String(entry.year).slice(2)}`,
			monthNum: entry.month,
			year: entry.year,
			key,
			isPast: true,
			expired: entry.expired,
			upcoming: entry.upcoming,
		}))
		.sort((a, b) => a.key.localeCompare(b.key));

	return [...pastMonths, ...futureMonths];
}
